/**
 * An asynchronous, batching event queue that keeps analytics off the
 * interaction path.
 *
 * The problem it solves: every destination does some work per event. gtag
 * builds a hit and calls `sendBeacon`; a PostHog sender opens a `fetch`; under
 * Tag Manager one push fans out to six vendors, each running its own handler.
 * None of that is expensive alone, but it runs on the main thread inside the
 * click handler, the route transition or the render effect that fired the
 * event, and it multiplies with every vendor added. The user paid for it with
 * frame budget on the thing they were actually doing.
 *
 * The shape of the fix is the standard one: `enqueue` is O(1) and returns
 * immediately, a scheduler flushes the queue when the host is idle (bounded by
 * a maximum delay so nothing waits more than about a second), and a flush hands
 * the sink a *batch* so a network sender can make one request instead of one
 * per event.
 *
 * Two deliberate exceptions to "defer everything":
 *
 *   - **Critical events dispatch synchronously.** `cta_click` fires immediately
 *     before a cross-document navigation, `checkout_started` immediately before
 *     a redirect to the payment host, `purchase` is the one event nobody may
 *     lose. A deferred callback is not guaranteed to run before the document
 *     goes away, so these bypass the queue and reach the sink inside the same
 *     call. The sink itself is still non-blocking (a `dataLayer.push`, a
 *     `keepalive` fetch), so the cost is microseconds.
 *   - **`flush()` is exposed** so the host can drain on `pagehide` and on app
 *     backgrounding, which is how the deferred events survive navigation.
 *
 * Platform-agnostic on purpose: the scheduler is injected, so the web passes
 * `requestIdleCallback`, React Native passes `InteractionManager`, and tests
 * pass a fake they can step by hand.
 */

export type QueuedAnalyticsEvent<TPayload> = {
  readonly name: string;
  readonly payload: TPayload;
  /**
   * Epoch milliseconds at enqueue time. Sinks that send a timestamp must use
   * this rather than the time of the flush: a batched event can be sent a
   * second and a half after it happened, and a funnel built on send-time
   * ordering would show steps out of order.
   */
  readonly enqueuedAt: number;
};

export type AnalyticsSink<TPayload> = (
  batch: readonly QueuedAnalyticsEvent<TPayload>[],
) => void | Promise<void>;

export type IdleScheduler = {
  /**
   * Runs `fn` exactly once, when the host is idle or after `timeoutMs`,
   * whichever comes first. Returns a function that cancels the pending run.
   */
  schedule(fn: () => void, timeoutMs: number): () => void;
};

export type AnalyticsQueueOptions<TPayload> = {
  sink: AnalyticsSink<TPayload>;
  scheduler: IdleScheduler;
  /** Events per sink call. A full batch also triggers a flush. Default 25. */
  maxBatchSize?: number;
  /** Longest an event may wait before a flush is forced. Default 1500 ms. */
  maxDelayMs?: number;
  /**
   * Ceiling on queued events. Beyond it the oldest are dropped, on the grounds
   * that a queue this deep means the host is not getting idle time, and the
   * newest events describe the state the user is actually in. Default 200.
   */
  maxQueueSize?: number;
  /** Clock, for tests. Default `Date.now`. */
  now?: () => number;
  /**
   * Receives sink failures. Analytics must never throw into product code, so
   * the queue swallows every error and reports it here instead. Default: drop.
   */
  onError?: (error: unknown) => void;
};

export type AnalyticsQueue<TPayload> = {
  /**
   * Adds an event. Returns immediately. With `critical: true` the event skips
   * the queue and reaches the sink synchronously, as a batch of one.
   */
  enqueue(
    name: string,
    payload: TPayload,
    options?: { critical?: boolean },
  ): void;
  /** Drains everything queued to the sink now, cancelling any pending run. */
  flush(): void;
  /** Discard deferred events after withdrawal, without disabling future grants. */
  clear(): void;
  /** Number of events waiting. */
  size(): number;
  /** Number of events dropped to the size ceiling since creation. */
  dropped(): number;
  /** Cancels pending work and discards the queue. Does not flush. */
  dispose(): void;
};

const noop = () => {};

export function createAnalyticsQueue<TPayload>(
  options: AnalyticsQueueOptions<TPayload>,
): AnalyticsQueue<TPayload> {
  const maxBatchSize = Math.max(1, options.maxBatchSize ?? 25);
  const maxDelayMs = Math.max(0, options.maxDelayMs ?? 1500);
  const maxQueueSize = Math.max(maxBatchSize, options.maxQueueSize ?? 200);
  const now = options.now ?? (() => Date.now());
  const onError = options.onError ?? noop;

  let queue: QueuedAnalyticsEvent<TPayload>[] = [];
  let cancelScheduled: (() => void) | null = null;
  let droppedCount = 0;
  let disposed = false;

  function deliver(batch: readonly QueuedAnalyticsEvent<TPayload>[]) {
    if (!batch.length) return;
    try {
      const result = options.sink(batch);
      if (result && typeof (result as Promise<void>).catch === "function") {
        (result as Promise<void>).catch(onError);
      }
    } catch (error) {
      onError(error);
    }
  }

  function cancelPending() {
    if (cancelScheduled) {
      try {
        cancelScheduled();
      } catch {
        // A scheduler whose cancel throws must not take the queue down with it.
      }
      cancelScheduled = null;
    }
  }

  function flush() {
    cancelPending();
    if (!queue.length) return;
    // Swap first so a sink that enqueues (or throws) cannot interfere with the
    // batch being drained.
    const draining = queue;
    queue = [];
    for (let index = 0; index < draining.length; index += maxBatchSize) {
      deliver(draining.slice(index, index + maxBatchSize));
    }
  }

  function ensureScheduled(timeoutMs: number) {
    if (cancelScheduled || disposed) return;
    cancelScheduled = options.scheduler.schedule(() => {
      cancelScheduled = null;
      flush();
    }, timeoutMs);
  }

  return {
    enqueue(name, payload, enqueueOptions) {
      if (disposed) return;
      const item: QueuedAnalyticsEvent<TPayload> = {
        name,
        payload,
        enqueuedAt: now(),
      };

      if (enqueueOptions?.critical) {
        deliver([item]);
        return;
      }

      queue.push(item);
      if (queue.length > maxQueueSize) {
        droppedCount += queue.length - maxQueueSize;
        queue = queue.slice(queue.length - maxQueueSize);
      }

      if (queue.length >= maxBatchSize) {
        // A full batch is worth sending now, but still not inside the caller's
        // stack: a zero-timeout schedule yields to the interaction first.
        cancelPending();
        ensureScheduled(0);
        return;
      }
      ensureScheduled(maxDelayMs);
    },
    flush,
    clear() {
      cancelPending();
      queue = [];
    },
    size: () => queue.length,
    dropped: () => droppedCount,
    dispose() {
      disposed = true;
      cancelPending();
      queue = [];
    },
  };
}

/**
 * Host access without depending on DOM or React Native type libraries: this
 * package is also compiled for the API. Every field is optional and probed at
 * call time.
 */
type IdleHost = {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number;
  cancelIdleCallback?: (handle: number) => void;
  setTimeout: (callback: () => void, delayMs: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  addEventListener?: (type: string, listener: () => void) => void;
  document?: {
    visibilityState?: string;
    addEventListener?: (type: string, listener: () => void) => void;
  };
};

const host = globalThis as unknown as IdleHost;

/**
 * A scheduler for browsers.
 *
 * Prefers `requestIdleCallback` with a timeout, which runs when the main thread
 * has nothing better to do and no later than `timeoutMs`. Safari has never
 * shipped it, so the fallback is a short `setTimeout`: it cannot detect idle,
 * but a small delay at least lets the interaction that fired the event finish
 * its own work first.
 */
export function createBrowserIdleScheduler(
  fallbackDelayMs = 200,
): IdleScheduler {
  return {
    schedule(fn, timeoutMs) {
      if (
        typeof host.requestIdleCallback === "function" &&
        typeof host.cancelIdleCallback === "function"
      ) {
        const handle = host.requestIdleCallback(fn, { timeout: timeoutMs });
        return () => host.cancelIdleCallback?.(handle);
      }
      const handle = host.setTimeout(fn, Math.min(timeoutMs, fallbackDelayMs));
      return () => host.clearTimeout(handle);
    },
  };
}

/**
 * Flushes when the page is about to go away, which is how deferred events
 * survive a navigation.
 *
 * `pagehide` is the reliable unload signal on every current browser and fires
 * for back/forward cache entries too; `visibilitychange` to `hidden` covers tab
 * switches and mobile app backgrounding, where iOS Safari does not always fire
 * `pagehide` at all. Idempotent per `flush` reference.
 */
const installedFlushHooks = new WeakSet<() => void>();

export function installFlushOnPageHide(flush: () => void) {
  if (installedFlushHooks.has(flush)) return;
  if (typeof host.addEventListener !== "function") return;
  installedFlushHooks.add(flush);

  host.addEventListener("pagehide", flush);
  host.document?.addEventListener?.("visibilitychange", () => {
    if (host.document?.visibilityState === "hidden") flush();
  });
}
